import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { scroller } from 'react-scroll';
import {
  triggerEditPageChangesMadeAnalytics,
  triggerSearchShopAnalytics,
  triggerSearchShopByLocationAnalytics,
} from '~/analytics/analytics.listener';
import { appointmentDraftActions, journeyStateActions, shopsActions } from '~/api';
import { buildSearchResultTitle } from '~/components/shops-search-form/helpers';
import { apiCallFinished, envConfigSelectors } from '../index';
import { Title } from '../layout/title';
import {
  errorMessages,
  errorMessagesActions,
  errorTypes,
  scrollToMessageTypeAnchor,
} from '../messages';
import { SearchForm, SearchFormContext } from '../search-form';
import { SearchResults } from './components';
import styles from './ShopsSearchForm.scss';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ title, linedStyles }) => (
  <Title
    className={[styles[linedStyles], styles.Title].join(' ').trim()}
    data-testid="search-form-title"
  >
    {title}
  </Title>
));

const { SHOPS_NOT_FOUND_ERROR, NO_SHOP_SEARCH_MADE_ERROR } = errorTypes;

export class ShopsSearchForm extends PureComponent {
  state = {
    isCurrentLocationLoading: false,
  };

  componentDidMount() {
    const { dispatchApiCallFinished, dispatchClearErrorMessages } = this.props;

    dispatchClearErrorMessages();
    this.loadShopsFromReduxStore().then(() => dispatchApiCallFinished(true));
  }

  loadShopsFromReduxStore = () => {
    const { storeSearchQuery, dispatchLoadShops, dispatchApiCallFinished } = this.props;
    if (storeSearchQuery.length > 0) {
      dispatchApiCallFinished(false);

      return dispatchLoadShops(storeSearchQuery);
    }
    return Promise.resolve();
  };

  onLoadShopsFinished = () => {
    scroller.scrollTo('search-results-title', {
      smooth: true,
      offset: -16,
    });
  };

  loadShopsHandler = (searchQuery) => {
    const {
      dispatchLoadShops,
      dispatchSetSearchQuery,
      dispatchResetErrorMessagesWith,
      dispatchClearErrorMessages,
      dispatchApiCallFinished,
      onShopsLoadedHandler,
      dispatchUpdateDraftAppointmentType,
      resetSelectedAppointmentType,
    } = this.props;

    dispatchApiCallFinished(false);
    onShopsLoadedHandler();
    dispatchSetSearchQuery(searchQuery);
    dispatchLoadShops(searchQuery).then(({ shops }) => {
      const message = errorMessages[SHOPS_NOT_FOUND_ERROR];
      const actionMessage = {
        type: SHOPS_NOT_FOUND_ERROR,
        description: message.createDescription(searchQuery),
      };

      if (!shops.length) {
        dispatchResetErrorMessagesWith([actionMessage]);
      } else {
        dispatchClearErrorMessages();
        triggerSearchShopAnalytics();
      }

      if (resetSelectedAppointmentType) {
        dispatchUpdateDraftAppointmentType();
      }

      dispatchApiCallFinished(true);
      this.onLoadShopsFinished();
    });
  };

  loadShopsByCurrentLocationHandler = (coordinates) => {
    const {
      dispatchLoadShops,
      dispatchClearErrorMessages,
      dispatchSetSearchQuery,
      onShopsLoadedHandler,
      resetSelectedAppointmentType,
      dispatchUpdateDraftAppointmentType,
    } = this.props;

    this.setState({
      isCurrentLocationLoading: true,
    });
    onShopsLoadedHandler();

    dispatchClearErrorMessages();
    dispatchSetSearchQuery('');
    dispatchLoadShops(coordinates, true).then(({ postcode }) => {
      dispatchSetSearchQuery(postcode);
      if (resetSelectedAppointmentType) {
        dispatchUpdateDraftAppointmentType();
      }
      this.setState({
        isCurrentLocationLoading: false,
      });
      this.onLoadShopsFinished();
    });
    triggerSearchShopByLocationAnalytics();
  };

  selectAndContinue = (nextShop) => {
    const {
      editMode,
      nextPath,
      history,
      resetSelectedAppointmentType,

      dispatchSelectShop,
      dispatchSaveAppointmentDraft,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchSaveJourneyState,
      dispatchUpdateDraftAppointmentType,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchSelectShop(nextShop);
    if (resetSelectedAppointmentType) {
      dispatchUpdateDraftAppointmentType();
    }
    dispatchSaveAppointmentDraft()
      .then(() => dispatchSaveJourneyState())
      .then(() => dispatchUpdateAppointmentType())
      .then(() => {
        let path = nextPath;
        if (nextPath instanceof Function) {
          path = nextPath(nextShop.shortName);
        }
        dispatchApiCallFinished(true);
        history.pushWithToken(path);
      });

    if (editMode) {
      triggerEditPageChangesMadeAnalytics('appointments', 'appointments:shop');
    }
  };

  render() {
    const { isCurrentLocationLoading } = this.state;
    const {
      shops,
      storeSearchQuery,
      dispatchResetErrorMessagesWith,
      messages: Messages,
      children,
      editMode,
      searchResultTitle,
      searchResultDescription,
      shopAddress,
      additionalShopTitle,
      displaySearchResults,
      hideAddress,
      isRemote,
      beautyMessage,
      remoteWordingFeature,
      virtualMessagingAdditionalShopTitle,
      virtualMessagingSearchResultTitle,
      virtualMessagingSearchResultDescription,
    } = this.props;
    const { searchFormTitle } = this.context;

    const [searchResultMessage, searchFormMessage] = React.Children.toArray(children);

    const FKBVirtualWordingMessageIsShown = !isRemote && remoteWordingFeature.enabled;
    const FKBAdditionalShopTitle =
      FKBVirtualWordingMessageIsShown && virtualMessagingAdditionalShopTitle;
    const FKBSearchResultTitle =
      FKBVirtualWordingMessageIsShown &&
      virtualMessagingSearchResultTitle &&
      buildSearchResultTitle(storeSearchQuery, virtualMessagingSearchResultTitle);
    const FKBSearchResultDescription =
      FKBVirtualWordingMessageIsShown && virtualMessagingSearchResultDescription;

    return (
      <Fragment>
        <div className={styles.ShopSearchFormContainer}>
          <ScrollToMessageTypeAnchor
            title={searchFormTitle}
            linedStyles={editMode ? 'lined' : ''}
            forTypes={[NO_SHOP_SEARCH_MADE_ERROR, SHOPS_NOT_FOUND_ERROR]}
          />
          {(FKBAdditionalShopTitle || additionalShopTitle) && (
            <p className={styles.Description} data-testid="shops-search-form-description">
              {FKBAdditionalShopTitle || additionalShopTitle}
            </p>
          )}
          <SearchForm
            title="Enter a shop name, town or postcode"
            onSubmitHandler={this.loadShopsHandler}
            onSubmitByLocationHandler={this.loadShopsByCurrentLocationHandler}
            isLocationLoading={isCurrentLocationLoading}
            storeSearchQuery={storeSearchQuery}
            resetErrorMessagesWith={dispatchResetErrorMessagesWith}
          >
            <Messages forTypes={[SHOPS_NOT_FOUND_ERROR]} />
            {searchFormMessage}
          </SearchForm>
        </div>
        {displaySearchResults && shops.length > 0 && (
          <SearchResults
            shops={shops}
            searchResultTitle={FKBSearchResultTitle || searchResultTitle}
            searchResultDescription={FKBSearchResultDescription || searchResultDescription}
            shopAddress={shopAddress}
            onClickHandler={this.selectAndContinue}
            hideAddress={hideAddress}
            isRemote={isRemote}
            beautyMessage={beautyMessage}
          >
            {searchResultMessage}
          </SearchResults>
        )}
      </Fragment>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    shops: { shops },
    journeyState: { searchQuery: storeSearchQuery = '' },
    editAppointment: { editMode },
    services: {
      config: {
        additionalShopTitle,
        resetSelectedAppointmentType = false,
        hideAddress = false,
        virtualMessagingAdditionalShopTitle,
        virtualMessagingSearchResultTitle,
        virtualMessagingSearchResultDescription,
        beautyMessage,
      },
    },
    appointment: {
      draft: {
        appointmentType: { id: appointmentTypeId },
      },
    },
  } = state;

  const remoteSearchResultDescription =
    envConfigSelectors.searchResultDescription(appointmentTypeId)(state);
  const remoteSearchResultTitle = envConfigSelectors.searchResultTitle(appointmentTypeId)(state);
  const remoteShopAddress = envConfigSelectors.shopAddress(appointmentTypeId)(state);
  const remoteAdditionalShopTitle =
    envConfigSelectors.remoteAdditionalShopTitle(appointmentTypeId)(state);

  const searchResultTitle = buildSearchResultTitle(storeSearchQuery, remoteSearchResultTitle);

  return {
    shops,
    nextPath: editMode
      ? envConfigSelectors.editShopNextPath(appointmentTypeId)(state)
      : envConfigSelectors.shopsNextPath(appointmentTypeId)(state),
    storeSearchQuery,
    editMode,
    searchResultTitle,
    searchResultDescription: remoteSearchResultDescription,
    shopAddress: remoteShopAddress,
    additionalShopTitle: remoteAdditionalShopTitle || additionalShopTitle,
    resetSelectedAppointmentType,
    hideAddress,
    beautyMessage,
    isRemote: envConfigSelectors.isAppointmentTypeRemote(appointmentTypeId)(state),
    remoteWordingFeature: envConfigSelectors.remoteWordingFeature(state),
    virtualMessagingAdditionalShopTitle,
    virtualMessagingSearchResultTitle,
    virtualMessagingSearchResultDescription,
  };
};

const mapDispatchToProps = {
  dispatchLoadShops: shopsActions.loadShops,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchSaveAppointmentDraft: appointmentDraftActions.saveAppointmentDraft,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchUpdateDraftAppointmentType: appointmentDraftActions.updateDraftAppointmentType,
  dispatchSetSearchQuery: journeyStateActions.setSearchQuery,
  dispatchSaveJourneyState: journeyStateActions.saveJourneyState,
  dispatchApiCallFinished: apiCallFinished,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
};

export const ConnectedShopsSearchForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ShopsSearchForm));

ShopsSearchForm.contextType = SearchFormContext;

ShopsSearchForm.propTypes = {
  shops: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  storeSearchQuery: PropTypes.string.isRequired,
  editMode: PropTypes.bool.isRequired,
  searchResultTitle: PropTypes.string.isRequired,
  nextPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  resetSelectedAppointmentType: PropTypes.bool.isRequired,
  history: PropTypes.shape({}).isRequired,
  hideAddress: PropTypes.bool.isRequired,
  displaySearchResults: PropTypes.bool,
  shopAddress: PropTypes.string,
  additionalShopTitle: PropTypes.string,
  searchResultDescription: PropTypes.string,
  isRemote: PropTypes.bool.isRequired,
  beautyMessage: PropTypes.string,
  remoteWordingFeature: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
  }).isRequired,
  virtualMessagingAdditionalShopTitle: PropTypes.string,
  virtualMessagingSearchResultTitle: PropTypes.string,
  virtualMessagingSearchResultDescription: PropTypes.string,

  dispatchLoadShops: PropTypes.func.isRequired,
  onShopsLoadedHandler: PropTypes.func.isRequired,
  dispatchSetSearchQuery: PropTypes.func.isRequired,
  dispatchSaveJourneyState: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  messages: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  dispatchSaveAppointmentDraft: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchUpdateDraftAppointmentType: PropTypes.func.isRequired,
};

ShopsSearchForm.defaultProps = {
  additionalShopTitle: null,
  displaySearchResults: true,
  searchResultDescription: null,
  shopAddress: null,
  virtualMessagingAdditionalShopTitle: null,
  virtualMessagingSearchResultTitle: null,
  virtualMessagingSearchResultDescription: null,
};
